import { formatDateTime } from '../../../../utils/hubspotUtils';
import { LoadingSection } from '../../layout/LoadingSection';
import { PanelSection } from '../../PanelSection';
import type { HubspotTicketDetailData } from '../../types';

interface HubspotTicketAuditSectionProps {
  isLoading: boolean;
  auditEntries: HubspotTicketDetailData['auditEntries'];
}

function formatAuditAction(action: string) {
  switch (action) {
    case 'hubspotTicket.statusUpdated':
      return 'Status updated';
    case 'hubspotTicket.assignmentUpdated':
      return 'Assignment updated';
    default:
      return action
        .replace(/([A-Z])/g, ' $1')
        .replace(/\./g, ' ')
        .trim();
  }
}

function formatAuditLabel(key: string) {
  return key.replace(/([A-Z])/g, ' $1').trim();
}

function formatAuditValue(value: unknown) {
  if (value === null || value === undefined || value === '') {
    return '—';
  }

  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  if (typeof value === 'number') {
    return value.toString();
  }

  if (typeof value === 'string') {
    return value;
  }

  return JSON.stringify(value);
}

export function HubspotTicketAuditSection({
  isLoading,
  auditEntries,
}: HubspotTicketAuditSectionProps) {
  return (
    <LoadingSection isLoading={isLoading}>
      <PanelSection
        title="Audit trail"
        trailing={
          <span className="text-xs text-neutral-500">
            {auditEntries.length}{' '}
            {auditEntries.length === 1 ? 'entry' : 'entries'}
          </span>
        }
      >
        {auditEntries.length === 0 ? (
          <div className="text-sm text-neutral-500">
            No ticket workflow changes have been audited yet.
          </div>
        ) : (
          <ol className="relative space-y-4 border-l border-white/10 pl-4">
            {auditEntries.map((entry) => (
              <li key={entry.id} className="relative">
                <span
                  aria-hidden="true"
                  className="absolute -left-[21px] top-1.5 size-2 rounded-full bg-neutral-600 ring-2 ring-neutral-900"
                />
                <div className="flex flex-wrap items-baseline gap-x-2 text-xs">
                  <span className="font-medium text-neutral-200">
                    {formatAuditAction(entry.action)}
                  </span>
                  {entry.user && (
                    <span className="text-neutral-400">
                      by {entry.user.fullName}
                    </span>
                  )}
                  <span className="text-neutral-500">
                    {formatDateTime(entry.createdAt)}
                  </span>
                </div>
                {entry.changes && Object.keys(entry.changes).length > 0 ? (
                  <div className="mt-1.5 space-y-1">
                    {Object.entries(entry.changes).map(([key, value]) => (
                      <div
                        key={key}
                        className="grid gap-1 text-xs text-neutral-300 sm:grid-cols-[140px_1fr]"
                      >
                        <div className="text-neutral-500">
                          {formatAuditLabel(key)}
                        </div>
                        <div className="break-words">
                          <span className="text-neutral-500 line-through decoration-neutral-600">
                            {formatAuditValue(value.old)}
                          </span>{' '}
                          <span className="text-neutral-200">
                            {formatAuditValue(value.new)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="mt-1 text-xs text-neutral-500">
                    No field-level diff was recorded for this action.
                  </div>
                )}
              </li>
            ))}
          </ol>
        )}
      </PanelSection>
    </LoadingSection>
  );
}
